import type {
  NotificationPreferences,
  NotificationPreferencesOutput,
} from "@repo/contract/notifications";

/**
 * The reminder editor's pure half: A2's `ReminderSheet` (editors.tsx:850-906)
 * over `PUT /me/notification-preferences`.
 *
 * ── THE PERSON PICKS A LOCAL TIME; THE SERVER STORES A UTC MINUTE ───────────
 * `reminderUtcMinute` is a minute of the UTC day, and `utcOffsetMinutes` is
 * the device offset it was computed with (preferences.ts docblock). So:
 *
 * - SHOWN: the stored minute plus the STORED offset — the local hour the
 *   person actually chose, even if the phone has since changed zone.
 * - SAVED: the picked local minute minus the device's offset NOW, and that
 *   same offset sent beside it. One offset for both, or the reminder fires
 *   at the wrong hour.
 *
 * Both wrap around midnight: 21:30 in São Paulo (−180) is 00:30 UTC.
 *
 * ── THE WEEKDAYS ARE LOCAL, AND NEED NO CONVERSION ──────────────────────────
 * `reminderWeekdays` are the person's own days (`0` Sunday); the server
 * shifts the instant by the offset before asking which day it is. `null` is
 * every day; `[]` is refused by the contract, which the form reports as a
 * field error on `reminderWeekdays`.
 */

const MINUTES_PER_DAY = 1440;

/** A2's range and step: 05:00 to 23:45, every quarter hour. */
export const REMINDER_MIN_MINUTE = 5 * 60;
export const REMINDER_MAX_MINUTE = 23 * 60 + 45;
export const REMINDER_STEP_MINUTES = 15;
/** Offered when no hour was ever chosen. */
export const REMINDER_DEFAULT_MINUTE = 19 * 60;

const mod = (n: number, m: number) => ((n % m) + m) % m;

/** The device's offset east of UTC, in minutes, at `now`. */
export function deviceUtcOffset(now: Date): number {
  const offset = -now.getTimezoneOffset();
  return offset === 0 ? 0 : offset;
}

export function localMinuteFromUtc(utcMinute: number, offset: number): number {
  return mod(utcMinute + offset, MINUTES_PER_DAY);
}

export function utcMinuteFromLocal(
  localMinute: number,
  offset: number
): number {
  return mod(localMinute - offset, MINUTES_PER_DAY);
}

/** `HH:MM` for a minute of the day. */
export function clockOf(minute: number): string {
  const m = mod(Math.round(minute), MINUTES_PER_DAY);
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

export type ReminderDays = "custom" | "every" | "training";

export interface ReminderForm {
  /** The days chosen under `"custom"`, `0` Sunday. */
  readonly custom: readonly number[];
  readonly days: ReminderDays;
  /** The LOCAL minute of the day. */
  readonly localMinute: number;
}

const sortedUnique = (days: readonly number[]) =>
  [...new Set(days)].sort((a, b) => a - b);

const sameDays = (a: readonly number[], b: readonly number[]) =>
  sortedUnique(a).join() === sortedUnique(b).join();

const clampToRange = (minute: number) => {
  const stepped =
    Math.round(minute / REMINDER_STEP_MINUTES) * REMINDER_STEP_MINUTES;
  return Math.min(REMINDER_MAX_MINUTE, Math.max(REMINDER_MIN_MINUTE, stepped));
};

/**
 * The draft a stored preference set opens as. `trainingDays` is the training
 * config's `fixedWeekdays`: a stored list equal to it reads as "on training
 * days".
 */
export function reminderFormFrom(
  prefs: Pick<
    NotificationPreferencesOutput,
    "reminderUtcMinute" | "reminderWeekdays" | "utcOffsetMinutes"
  >,
  trainingDays: readonly number[]
): ReminderForm {
  const localMinute =
    prefs.reminderUtcMinute === null
      ? REMINDER_DEFAULT_MINUTE
      : clampToRange(
          localMinuteFromUtc(prefs.reminderUtcMinute, prefs.utcOffsetMinutes)
        );
  const stored = prefs.reminderWeekdays ?? null;
  if (stored === null) {
    return { custom: [], days: "every", localMinute };
  }
  if (trainingDays.length > 0 && sameDays(stored, trainingDays)) {
    return { custom: sortedUnique(stored), days: "training", localMinute };
  }
  return { custom: sortedUnique(stored), days: "custom", localMinute };
}

function weekdaysOf(
  form: ReminderForm,
  trainingDays: readonly number[]
): number[] | null {
  if (form.days === "every") {
    return null;
  }
  if (form.days === "training") {
    return trainingDays.length === 0 ? null : sortedUnique(trainingDays);
  }
  // Left as given, so an empty choice reaches the contract and comes back as
  // the `reminderWeekdays` field error instead of silently meaning "every".
  return sortedUnique(form.custom);
}

/**
 * The WHOLE preference set the `PUT` replaces (there is no PATCH): the two
 * toggles this editor does not show ride along from `prefs`. Saving a time
 * turns the reminder on — picking an hour is asking to be reminded; the
 * notifications section's switch is where it is turned off.
 *
 * Unvalidated: the caller runs it through `notificationPreferences`.
 */
export function reminderPayload(
  prefs: Pick<
    NotificationPreferencesOutput,
    "sorenessEnabled" | "summaryEnabled"
  >,
  form: ReminderForm,
  deviceOffset: number,
  trainingDays: readonly number[]
): NotificationPreferences {
  return {
    reminderEnabled: true,
    reminderUtcMinute: utcMinuteFromLocal(form.localMinute, deviceOffset),
    reminderWeekdays: weekdaysOf(form, trainingDays),
    sorenessEnabled: prefs.sorenessEnabled,
    summaryEnabled: prefs.summaryEnabled,
    utcOffsetMinutes: deviceOffset,
  };
}
